import {CommentBoxButton, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {ActivityHeaderHeadingProps} from '@github-ui/commenting/ActivityHeader'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ActivityHeader, CommentAuthorTypes} from '@github-ui/commenting/ActivityHeader'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CommentActions} from '@github-ui/commenting/CommentActions'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {VALUES} from '@github-ui/commenting/Values'
import {CopilotCodeReviewFeedback} from '@github-ui/copilot-code-review-feedback/CopilotCodeReviewFeedback'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {noop} from '@github-ui/noop'
import type {ReactionContent} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {ReactionViewerBase} from '@github-ui/reaction-viewer/ReactionViewerBase'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {ssrSafeLocation, ssrSafeWindow} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {StopIcon} from '@primer/octicons-react'
import {Banner, Flash, ConfirmationDialog, Link} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useBlockUserFromOrgMutation} from '@github-ui/pull-requests/use-block-user-from-org-mutation'
import {useDeleteReviewCommentMutation} from '@github-ui/pull-requests/use-delete-review-comment-mutation'
import {useHideCommentMutation} from '@github-ui/pull-requests/use-hide-comment-mutation'
import {useReactToCommentMutation} from '@github-ui/pull-requests/use-react-to-comment-mutation'
import {useUnblockUserFromOrgMutation} from '@github-ui/pull-requests/use-unblock-user-from-org-mutation'
import {useUnhideCommentMutation} from '@github-ui/pull-requests/use-unhide-comment-mutation'
import {useUpdateReviewCommentMutation} from '@github-ui/pull-requests/use-update-review-comment-mutation'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {RelayEnvironmentProvider} from 'react-relay/hooks'

import {useCommentAnchor} from '../../hooks/use-comment-anchor'
import {usePersistedDiffCommentData} from '../../hooks/use-persisted-comment-data'
import type {
  ApplySuggestedChangesValidationData,
  Comment,
  CommentAuthor,
  SuggestedChangesConfiguration,
  ViewerData,
} from '../../conversation-types'
import {validateSuggestedChange} from '../../util/validate-suggested-change'
import {ConversationCommentBox} from './ConversationCommentBox'
import styles from './ReviewThreadComment.module.css'
import {SuggestedChangeView} from './SuggestedChangeView'
import type {DisableDialogModeFn, EnterDialogModeFn} from '../../stores/slices/dialog-mode'
import type {FileKey, LineKey} from '../../types'
/* eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports --
   preexisting violation https://github.com/github/react-platform/issues/1135 */
import {relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
import {ScopedCommands} from '@github-ui/ui-commands'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {usePullRequestBasePath} from '@github-ui/pull-requests/pull-request-store-selectors'

type CommentState = 'editing' | 'hidden' | 'visible'

export interface ReviewThreadCommentProps {
  anchorPrefix?: string
  comment: Comment
  commentConnectionId?: string
  disableDialogMode?: DisableDialogModeFn
  enableDialogMode?: EnterDialogModeFn
  filePath: string
  gridCellId?: string | undefined
  hideActions?: boolean
  index?: number
  /**
   * If true, the comment will scroll itself into view and show a blue border
   * when the component renders with a hash that matches the comment.
   */
  isAnchorable?: boolean
  isFirstComment?: boolean
  isInlineComment: boolean
  isInDialogMode?: boolean | undefined
  isLastChild?: boolean
  isOutdated?: boolean
  isThreadResolved: boolean
  lineMarkersKey: LineKey | FileKey
  onDeleteLineComment?: () => void
  onQuoteReply?: (quotedText?: string) => void
  suggestedChangesConfig?: SuggestedChangesConfiguration
  applySuggestedChangesValidationData?: ApplySuggestedChangesValidationData
  threadCommentCount?: number
  threadId: string
  threadPositionNumber?: number
  viewerData?: ViewerData
  ghostUser?: CommentAuthor
  originalDiffPathUri?: string | null
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export function ReviewThreadComment({
  isAnchorable = false,
  index = 0,
  isInlineComment,
  isLastChild,
  isOutdated,
  isThreadResolved,
  lineMarkersKey,
  anchorPrefix = 'r',
  comment,
  disableDialogMode,
  enableDialogMode,
  filePath,
  gridCellId,
  hideActions,
  isFirstComment = false,
  isInDialogMode,
  onDeleteLineComment,
  onQuoteReply = noop,
  threadCommentCount,
  threadId,
  threadPositionNumber,
  suggestedChangesConfig,
  applySuggestedChangesValidationData,
  viewerData,
  ghostUser = VALUES.ghostUser,
  originalDiffPathUri,
  headingLevel = 'h3',
}: ReviewThreadCommentProps): React.ReactElement {
  const {addToast} = useToastContext()
  const [isEditing, setIsEditing] = useState(false)
  const [isMinimized, setIsMinimized] = useState(comment.isHidden)
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false)
  const [bodyText, setBodyText] = useState(comment.body)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>('')
  const basePath = usePullRequestBasePath()
  const {persistCommentToStorage, removePersistedCommentFromStorage} = usePersistedDiffCommentData({
    subjectId: basePath,
    filePath,
    fileLevelComment: comment.subjectType === 'FILE',
    threadId: comment.id,
    handlePersistedCommentExists: ({text}) => {
      if (!text) return
      setBodyText(text)
    },
  })
  const commentBoxRef = useRef<CommentBoxHandle>(null)
  const commentBodyRef = useRef<HTMLDivElement>(null)
  const reactionButtonRef = useRef<HTMLButtonElement>(null)
  const [visibilityErrorMessage, setVisibilityErrorMessage] = useState('')

  const focusCommentBox = () => {
    commentBoxRef.current?.focus()
  }

  useEffect(() => {
    if (isEditing) {
      const timeout = window.setTimeout(focusCommentBox)

      return () => {
        window.clearTimeout(timeout)
      }
    }
  }, [isEditing])

  const {mutate: deleteReviewCommentMutation} = useDeleteReviewCommentMutation({
    filePath,
    lineMarkersKey,
    onSuccess: () => {
      setDeleteConfirmationVisible(false)
      setIsDeleting(false)
      onDeleteLineComment?.()
    },
  })

  const onDelete = async () => {
    // when InlineCommentDialogMode is enabled, keyboard interactions are trapped in that div
    // we disable here so we can use keyboard interactions in the confirm dialog, which is outside the div
    disableDialogMode?.()
    setDeleteConfirmationVisible(true)
  }

  const handleDeleteDialogClose = (gesture: 'confirm' | 'cancel' | 'close-button' | 'escape') => {
    if (gesture === 'confirm') {
      setIsDeleting(true)

      deleteReviewCommentMutation(
        {commentId: comment.id, threadId, filePath},
        {
          onError: () => {
            setDeleteConfirmationVisible(false)
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: 'Failed to delete comment',
            })
            setIsDeleting(false)
          },
        },
      )
    } else {
      // when InlineCommentDialogMode is enabled, keyboard interactions are trapped in that div
      // we re-enable here so that keyboard interactions work again after the dialog is closed
      if (gridCellId) enableDialogMode?.(gridCellId)
      setDeleteConfirmationVisible(false)
      setIsDeleting(false)
    }
  }

  const {mutate: hideCommentMutation} = useHideCommentMutation(filePath, lineMarkersKey)

  const onHide = (reason: string) => {
    if (!comment.databaseId) throw new Error('Comment not found')

    hideCommentMutation(
      {commentDatabaseId: comment.databaseId, reason},
      {
        onError: () => {
          setIsSubmitting(false)
          setVisibilityErrorMessage('Failed to hide comment')
        },
        onSuccess: () => setIsMinimized(true),
      },
    )
  }

  const {mutate: unhideCommentMutation} = useUnhideCommentMutation(filePath, lineMarkersKey)

  const onUnhide = () => {
    if (!comment.databaseId) throw new Error('Comment not found')

    unhideCommentMutation(
      {commentDatabaseId: comment.databaseId},
      {
        onError: () => {
          setIsSubmitting(false)
          setVisibilityErrorMessage('Failed to unhide comment')
        },
        onSuccess: () => setIsMinimized(false),
      },
    )
  }

  const {mutate: blockUserFromOrgMutation} = useBlockUserFromOrgMutation()

  const onBlock = (
    duration: string,
    shouldHideComment: boolean,
    hiddenReason: string | undefined,
    organizationLogin: string,
    notifyBlockedUser: boolean,
    userLogin: string,
  ) =>
    blockUserFromOrgMutation(
      {
        duration,
        shouldHideComment,
        hiddenReason,
        organizationLogin,
        notifyBlockedUser,
        userLogin,
      },
      {
        onError: () => setVisibilityErrorMessage('Failed to block user'),
        onSuccess: () => {
          if (shouldHideComment) {
            setIsMinimized(true)
          }
        },
      },
    )

  const {mutate: unblockUserFromOrgMutation} = useUnblockUserFromOrgMutation()

  const onUnblock = (organizationLogin: string, userLogin: string) =>
    unblockUserFromOrgMutation(
      {organizationLogin, userLogin},
      {onError: () => setVisibilityErrorMessage('Failed to unblock user')},
    )

  const {mutate: reactToCommentMutation} = useReactToCommentMutation(filePath, lineMarkersKey)

  const onReact = (reaction: ReactionContent, viewerHasReacted: boolean) => {
    if (!comment.databaseId) throw new Error('Comment not found')

    setTimeout(() => reactionButtonRef.current?.focus(), 0)
    reactToCommentMutation(
      {
        commentDatabaseId: comment.databaseId,
        threadId,
        reaction,
        viewerHasReacted,
      },
      {
        onError: () => {
          setIsSubmitting(false)
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: 'Failed to react to comment',
          })
        },
      },
    )
  }

  const onEdit = () => {
    setIsEditing(true)
  }

  const onCancel = () => {
    setIsEditing(false)
    removePersistedCommentFromStorage()
    setBodyText(comment.body)
  }

  const onChange = (newBody: string) => {
    persistCommentToStorage({text: newBody})
    setBodyText(newBody)
    setErrorMessage(undefined)
  }

  const {mutate: updateReviewCommentMutation} = useUpdateReviewCommentMutation({
    filePath,
    lineMarkersKey,
    onSuccess: () => {
      setIsEditing(false)
      setIsSubmitting(false)
      removePersistedCommentFromStorage()
    },
  })

  const onSave = useCallback(() => {
    setIsSubmitting(true)

    const suggestedChangedEvaluation = validateSuggestedChange(
      bodyText,
      suggestedChangesConfig?.sourceContentFromDiffLines ?? '',
    )
    if (!suggestedChangedEvaluation.isValid) {
      setErrorMessage(suggestedChangedEvaluation.errorMessage)
      setIsSubmitting(false)

      return
    }

    updateReviewCommentMutation(
      {commentId: String(comment.databaseId), body: bodyText, bodyVersion: comment.bodyVersion},
      {
        onError: () => {
          setIsSubmitting(false)
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: 'Failed to update comment',
          })
        },
      },
    )
  }, [addToast, bodyText, comment, suggestedChangesConfig?.sourceContentFromDiffLines, updateReviewCommentMutation])

  // The unique identifier for this comment in the DOM
  const commentId = comment.databaseId?.toString()

  // The anchor portion of the URL that will target this specific comment when navigating to it
  // This is used to scroll the comment into view and highlight it when directly linked
  const commentAnchor = commentId ? `${anchorPrefix}${commentId}` : undefined

  // The full URL including anchor that points directly to this comment within the current page
  const pageScopedCommentHref =
    commentAnchor && ssrSafeLocation
      ? new URL(`${ssrSafeLocation.pathname}#${commentAnchor}`, ssrSafeLocation.origin).toString()
      : ''
  const containerRef = useRef<HTMLDivElement>(null)

  useCommentAnchor({
    isAnchorable,
    commentId,
    commentAnchor,
    containerRef,
  })

  const isReply = index > 0
  const isNestedReply = index > 1

  const commentState: CommentState = isEditing ? 'editing' : isMinimized ? 'hidden' : 'visible'

  const commentActivityProps = {
    headingProps: {as: headingLevel} as ActivityHeaderHeadingProps,
    avatarUrl: comment.author?.avatarUrl ?? ghostUser.avatarUrl,
    comment: {
      ...comment,
      url: pageScopedCommentHref,
      referenceText: comment.reference.text ?? `#${comment.reference.number}`,
    },
    commentRef: containerRef,
    deleteComment: onDelete,
    editComment: onEdit,
    editHistoryComponent: undefined,
    hideComment: onHide,
    onBlock,
    onUnblock,
    onMinimize: setIsMinimized,
    onSuccessfulBlock: () => {
      //not ideal, but this is the easiest way to get all of the comments to reflect the newly updated state
      setTimeout(() => ssrSafeWindow?.location.reload(), 800)
    },
    isMinimized,
    commentAuthorLogin: comment.author?.login ?? ghostUser.login,
    navigate: noop,
    commentSubjectAuthorLogin: comment.reference?.author?.login ?? '',
    onReplySelect: onQuoteReply,
    unhideComment: onUnhide,
    hideActions,
    isReply,
    forceInlineAvatar: true,
    containerStyle: {},
    originalDiffPathUri,
    isOutdated,
    commentAuthorSlug: comment.reviewVariantType === 'copilot' ? 'copilot-pull-request-reviewer' : undefined,
    commentAuthorType: comment.reviewVariantType === 'copilot' ? CommentAuthorTypes.Bot : undefined,
  }

  const getCommentAriaLabel = (): string => {
    if (isReply) {
      return threadPositionNumber !== undefined ? `Reply ${index} to Comment ${threadPositionNumber}` : `Reply ${index}`
    } else if (threadPositionNumber !== undefined) {
      return `Comment ${threadPositionNumber}`
    } else {
      return 'Comment'
    }
  }

  const quoteReplyCommand = () => {
    if (isEditing) {
      // Don't trigger a reply when typing 'r' while editing
      return
    }

    const selection = ssrSafeWindow?.getSelection()

    const selectionContext = selection?.anchorNode
      ? {anchorNode: selection.anchorNode, range: selection.getRangeAt(0)}
      : null

    const newValue = selectQuoteFromComment(containerRef?.current, selectionContext, comment.body)
    onQuoteReply(newValue)
  }

  const environment = useMemo(() => relayEnvironmentWithMissingFieldHandlerForNode(), [])

  return (
    <ScopedCommands
      commands={{
        'pull-requests-diff-view:quote-reply': quoteReplyCommand,
      }}
    >
      <div
        ref={containerRef}
        id={isAnchorable ? commentAnchor : undefined}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- tabIndex needed for keyboard navigation in grid
        tabIndex={isInlineComment ? (isInDialogMode ? 0 : -1) : 0}
        data-first-thread-comment={isFirstComment}
        data-marker-navigation-comment-thread-id={threadId}
        data-marker-navigation-comment-id={comment.id}
        // When aria-label and role is set while still in gridcell mode, the browser doesn't the respect the
        // aria-hidden"true" and we end up with the aria-labels leaking into the accessible name of the gridcell,
        // which is not what we want. So we should only set these attributes while in dialog mode.
        {...(isInDialogMode
          ? {
              role: 'document',
              'aria-roledescription': 'comment',
              'aria-label': getCommentAriaLabel(),
            }
          : {})}
        className={clsx(
          styles.ReviewThreadContainer,
          isAnchorable && styles.anchorable,
          commentState === 'hidden' && 'pb-2',
          isReply && styles.isReply,
        )}
      >
        {isNestedReply && (
          <div className={styles.ReviewThreadWrapper} data-is-reply={isReply}>
            <div className={styles.ReviewThreadBox} />
          </div>
        )}
        <div className={styles.ReviewThreadInnerContainer} data-is-nested-reply={isNestedReply}>
          {comment.reviewVariantType === 'code_scanning' &&
            isFeatureEnabled('code_scanning_dfa_degraded_experience_notice') && (
              <Banner variant="info" hideTitle title="Code scanning notice" className="mt-2 pr-mb-3">
                <Banner.Description>
                  <Link inline href={basePath}>
                    View in Conversation tab
                  </Link>{' '}
                  for the full code scanning experience.
                </Banner.Description>
              </Banner>
            )}
          <ActivityHeader
            {...commentActivityProps}
            commentBody={comment.body}
            threadCommentCount={threadCommentCount}
            isInDialogMode={isInDialogMode}
            actions={
              hideActions ? undefined : (
                <RelayEnvironmentProvider environment={environment}>
                  <CommentActions {...commentActivityProps} />
                </RelayEnvironmentProvider>
              )
            }
            isOutdated={isOutdated}
          />
        </div>
        {visibilityErrorMessage && (
          <Flash variant="danger" className="pr-m-3">
            <StopIcon className="mr-2" />
            {visibilityErrorMessage}
          </Flash>
        )}
        <div className={styles.ReviewThreadWrapper} data-is-reply={isReply}>
          {isReply && <div className={styles.ReplyWrapper} data-is-last-child={isLastChild} />}

          {commentState === 'editing' && (
            <div className={clsx('flex-1 min-width-0 pr-2 pt-1 pb-2', isReply ? 'pl-0' : 'pl-2')}>
              <ConversationCommentBox
                ref={commentBoxRef}
                label="Update comment"
                value={bodyText}
                onChange={onChange}
                onPrimaryAction={onSave}
                suggestedChangesConfig={suggestedChangesConfig}
                markdownErrorMessage={errorMessage}
              >
                <CommentBoxButton variant="default" onClick={onCancel}>
                  Cancel
                </CommentBoxButton>
                <CommentBoxButton
                  disabled={isSubmitting || !bodyText.length}
                  loading={isSubmitting}
                  variant="primary"
                  onClick={onSave}
                >
                  Update
                </CommentBoxButton>
              </ConversationCommentBox>
            </div>
          )}

          {commentState === 'visible' && (
            <div className={styles.BodyHTMLContainer} data-is-reply={isReply}>
              {comment.bodyHTML && (
                <>
                  <VerifiedHTMLBox
                    ref={commentBodyRef}
                    className={clsx('markdown-body', styles.SafeHTMLBox, styles.SafeHTMLBoxWrapped)}
                    comment-testid={`Comment body html for comment ${comment.id}`}
                    /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string */
                    html={comment.bodyHTML as SafeHTMLString}
                  />
                  {comment.reviewVariantType === 'copilot' && (
                    <p className="text-small color-fg-muted mt-2 mb-1">
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        className="Link--inTextBlock"
                        href="https://docs.github.com/en/copilot/responsible-use-of-github-copilot-features/responsible-use-of-github-copilot-code-review"
                      >
                        Copilot
                      </a>{' '}
                      uses AI. Check for mistakes.
                    </p>
                  )}
                  <div className="d-flex flex-direction-column gap-2">
                    {comment.reviewVariantType === 'copilot' && (
                      <div className="mt-2">
                        <CopilotCodeReviewFeedback
                          commentUrl={comment.url}
                          commentId={comment.databaseId?.toString() ?? ''}
                        />
                      </div>
                    )}
                    {comment.reactionGroups && (
                      <div className="d-flex flex-direction-column mt-2">
                        <ReactionViewerBase
                          ref={reactionButtonRef}
                          reactionGroups={comment.reactionGroups}
                          onReact={onReact}
                          canReact={comment.viewerCanReact}
                        />
                      </div>
                    )}
                  </div>
                  {applySuggestedChangesValidationData && (
                    <SuggestedChangeView
                      comment={comment}
                      commentBodyRef={commentBodyRef}
                      filePath={filePath}
                      isOutdated={isOutdated}
                      isThreadResolved={isThreadResolved}
                      databaseId={comment.databaseId}
                      suggestedChangesConfig={suggestedChangesConfig}
                      applySuggestedChangesValidationData={applySuggestedChangesValidationData}
                      viewerCanApplySuggestion={viewerData?.viewerCanApplySuggestion}
                    />
                  )}
                </>
              )}
              {deleteConfirmationVisible && (
                <ConfirmationDialog
                  title="Delete comment?"
                  confirmButtonContent="Delete"
                  confirmButtonType="danger"
                  confirmButtonLoading={isDeleting}
                  onClose={handleDeleteDialogClose}
                >
                  Are you sure you want to delete this comment?
                </ConfirmationDialog>
              )}
            </div>
          )}
        </div>
      </div>
    </ScopedCommands>
  )
}
